import ResourcesIds from '../../../resources/ResourcesIds';

/**
 * The terms document as structure, with the words living in the locale files.
 *
 * Kept as data rather than as JSX so that the page component stays a renderer: adding a
 * clause is a key in `public/locales/{ro,en}` plus an entry here, and neither touches
 * layout. The order of this array is the order of the document and of its contents list.
 *
 * Section ids are numbers, not slugs, because a slug would have to be either Romanian or
 * English and the anchors have to survive a language switch.
 */
export type TermsBlock =
    | { kind: 'paragraph'; textId: string }
    | { kind: 'list'; itemIds: string[] }
    /** The two consumer bodies, drawn as external links rather than as plain text. */
    | { kind: 'consumerLinks' }
    /** Who publishes the document -- from `termsOperator`, not from a locale file. */
    | { kind: 'operatorDetails' };

export interface TermsSection {
    /** Anchor target and contents-list number, e.g. `terms-3`. */
    id: string;
    titleId: string;
    blocks: TermsBlock[];
}

const paragraph = (textId: string): TermsBlock => ({ kind: 'paragraph', textId });

export const termsSections: TermsSection[] = [
    {
        id: 'terms-1',
        titleId: ResourcesIds.TERMS_S1_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S1_P1),
            paragraph(ResourcesIds.TERMS_S1_P2),
            paragraph(ResourcesIds.TERMS_S1_P3),
            {
                kind: 'list',
                itemIds: [
                    ResourcesIds.TERMS_S1_LI1,
                    ResourcesIds.TERMS_S1_LI2,
                    ResourcesIds.TERMS_S1_LI3,
                    ResourcesIds.TERMS_S1_LI4,
                    ResourcesIds.TERMS_S1_LI5,
                ],
            },
        ],
    },
    {
        id: 'terms-2',
        titleId: ResourcesIds.TERMS_S2_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S2_P1),
            paragraph(ResourcesIds.TERMS_S2_P2),
            paragraph(ResourcesIds.TERMS_S2_P3),
            paragraph(ResourcesIds.TERMS_S2_P4),
        ],
    },
    {
        id: 'terms-3',
        titleId: ResourcesIds.TERMS_S3_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S3_P1),
            paragraph(ResourcesIds.TERMS_S3_P2),
            paragraph(ResourcesIds.TERMS_S3_P3),
            paragraph(ResourcesIds.TERMS_S3_P4),
            paragraph(ResourcesIds.TERMS_S3_P5),
            paragraph(ResourcesIds.TERMS_S3_P6),
        ],
    },
    {
        id: 'terms-4',
        titleId: ResourcesIds.TERMS_S4_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S4_P1),
            paragraph(ResourcesIds.TERMS_S4_P2),
            paragraph(ResourcesIds.TERMS_S4_P3),
            paragraph(ResourcesIds.TERMS_S4_P4),
            paragraph(ResourcesIds.TERMS_S4_P5),
        ],
    },
    {
        id: 'terms-5',
        titleId: ResourcesIds.TERMS_S5_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S5_P1),
            paragraph(ResourcesIds.TERMS_S5_P2),
            paragraph(ResourcesIds.TERMS_S5_P3),
            paragraph(ResourcesIds.TERMS_S5_P4),
            paragraph(ResourcesIds.TERMS_S5_P5),
        ],
    },
    {
        id: 'terms-6',
        titleId: ResourcesIds.TERMS_S6_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S6_P1),
            paragraph(ResourcesIds.TERMS_S6_P2),
            paragraph(ResourcesIds.TERMS_S6_P3),
            paragraph(ResourcesIds.TERMS_S6_P4),
            paragraph(ResourcesIds.TERMS_S6_P5),
        ],
    },
    {
        id: 'terms-7',
        titleId: ResourcesIds.TERMS_S7_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S7_P1),
            paragraph(ResourcesIds.TERMS_S7_P2),
            paragraph(ResourcesIds.TERMS_S7_P3),
            paragraph(ResourcesIds.TERMS_S7_P4),
            paragraph(ResourcesIds.TERMS_S7_P5),
        ],
    },
    {
        id: 'terms-8',
        titleId: ResourcesIds.TERMS_S8_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S8_P1),
            paragraph(ResourcesIds.TERMS_S8_P2),
            paragraph(ResourcesIds.TERMS_S8_P3),
            paragraph(ResourcesIds.TERMS_S8_P4),
            paragraph(ResourcesIds.TERMS_S8_P5),
        ],
    },
    {
        id: 'terms-9',
        titleId: ResourcesIds.TERMS_S9_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S9_P1),
            {
                kind: 'list',
                itemIds: [
                    ResourcesIds.TERMS_S9_LI1,
                    ResourcesIds.TERMS_S9_LI2,
                    ResourcesIds.TERMS_S9_LI3,
                    ResourcesIds.TERMS_S9_LI4,
                    ResourcesIds.TERMS_S9_LI5,
                ],
            },
            paragraph(ResourcesIds.TERMS_S9_P2),
        ],
    },
    {
        id: 'terms-10',
        titleId: ResourcesIds.TERMS_S10_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S10_P1),
            paragraph(ResourcesIds.TERMS_S10_P2),
            paragraph(ResourcesIds.TERMS_S10_P3),
            paragraph(ResourcesIds.TERMS_S10_P4),
            paragraph(ResourcesIds.TERMS_S10_P5),
        ],
    },
    {
        id: 'terms-11',
        titleId: ResourcesIds.TERMS_S11_TITLE,
        blocks: [paragraph(ResourcesIds.TERMS_S11_P1), paragraph(ResourcesIds.TERMS_S11_P2)],
    },
    {
        id: 'terms-12',
        titleId: ResourcesIds.TERMS_S12_TITLE,
        blocks: [paragraph(ResourcesIds.TERMS_S12_P1), paragraph(ResourcesIds.TERMS_S12_P2)],
    },
    {
        id: 'terms-13',
        titleId: ResourcesIds.TERMS_S13_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S13_P1),
            paragraph(ResourcesIds.TERMS_S13_P2),
            paragraph(ResourcesIds.TERMS_S13_P3),
            { kind: 'consumerLinks' },
        ],
    },
    {
        id: 'terms-14',
        titleId: ResourcesIds.TERMS_S14_TITLE,
        blocks: [
            paragraph(ResourcesIds.TERMS_S14_P1),
            { kind: 'operatorDetails' },
            paragraph(ResourcesIds.TERMS_S14_P2),
        ],
    },
];
